/*! JointJS+ v4.2.2 (2026-01-22) - HTML5 Diagramming Framework

Copyright (c) 2025 client IO

This Source Code Form is subject to the terms of the JointJS+
License, v. 2.0. If a copy of the JointJS+ License was not
distributed with this file, You can obtain one at
https://www.jointjs.com/license or from the JointJS+ archive as was
distributed by client IO. See the LICENSE file.
*/
var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { dia, ui, util } from '@joint/plus';
export var ZOOM_SETTINGS = {
    min: 0.2,
    max: 2
};
var baseUrl = 'assets/navigator';
var IconButton = ui.widgets.button.extend({
    render: function () {
        var size = this.options.size || 20;
        var imageEl = document.createElement('img');
        imageEl.style.width = "".concat(size, "px");
        imageEl.style.height = "".concat(size, "px");
        this.el.appendChild(imageEl);
        this.setIcon(this.options.icon);
        this.setTooltip(this.options.tooltip);
        return this;
    },
    setIcon: function (icon) {
        if (icon === void 0) { icon = ''; }
        this.el.querySelector('img').src = icon;
    },
    setTooltip: function (tooltip) {
        if (tooltip === void 0) { tooltip = ''; }
        this.el.dataset.tooltip = tooltip;
    }
});
// Simplified navigator element view
var UpdateFlags = {
    Render: '@render',
    Update: '@update',
    Transform: '@transform'
};
var NavigatorElementView = dia.ElementView.extend({
    body: null,
    markup: util.svg /* xml */(templateObject_1 || (templateObject_1 = __makeTemplateObject(["<path @selector=\"body\" opacity=\"0.4\" />"], [/* xml */ "<path @selector=\"body\" opacity=\"0.4\" />"]))),
    // updates run on view initialization
    initFlag: [UpdateFlags.Render, UpdateFlags.Update, UpdateFlags.Transform],
    // updates run when the model attribute changes
    presentationAttributes: {
        position: [UpdateFlags.Transform],
        angle: [UpdateFlags.Transform],
        size: [UpdateFlags.Update], // shape
    },
    // calls in an animation frame after a multiple changes
    // has been made to the model
    confirmUpdate: function (flags) {
        if (this.hasFlag(flags, UpdateFlags.Render))
            this.render();
        if (this.hasFlag(flags, UpdateFlags.Update))
            this.update();
        // using the original `updateTransformation()` method
        if (this.hasFlag(flags, UpdateFlags.Transform))
            this.updateTransformation();
    },
    render: function () {
        var doc = util.parseDOMJSON(this.markup);
        this.body = doc.selectors.body;
        this.body.classList.add(this.model.get('group'));
        this.el.appendChild(doc.fragment);
    },
    update: function () {
        var _a = this, model = _a.model, body = _a.body;
        // shape
        var _b = model.size(), width = _b.width, height = _b.height;
        var d = "M 0 0 H ".concat(width, " V ").concat(height, " H 0 Z");
        body.setAttribute('d', d);
    }
});
var NavigatorService = /** @class */ (function () {
    function NavigatorService(element) {
        this.element = element;
        this.transitionCanceled = false;
    }
    NavigatorService.prototype.create = function (scroller) {
        var _this = this;
        this.scroller = scroller;
        this.toolbar = new ui.Toolbar({
            autoToggle: true,
            references: {
                paperScroller: scroller
            },
            tools: [
                {
                    type: 'icon-button',
                    name: 'fullscreen'
                    /* icon and tooltip are set in updateToolbarButtons() */
                },
                {
                    type: 'icon-button',
                    name: 'fit-to-screen',
                    icon: "".concat(baseUrl, "/fit-to-screen.svg"),
                    tooltip: 'Fit to screen'
                },
                {
                    type: 'zoom-slider',
                    min: ZOOM_SETTINGS.min * 100,
                    max: ZOOM_SETTINGS.max * 100,
                    step: 10,
                    attrs: { input: { 'data-tooltip': 'Slide to zoom' } }
                },
                { type: 'separator' },
                {
                    type: 'icon-button',
                    name: 'minimap',
                    icon: "".concat(baseUrl, "/minimap.svg"),
                }
            ],
            widgetNamespace: __assign(__assign({}, ui.widgets), { iconButton: IconButton })
        });
        this.toolbar.render();
        this.updateToolbarButtons();
        this.element.appendChild(this.toolbar.el);
        this.toolbar.on('fit-to-screen:pointerclick', function () { return _this.fitToScreen(); });
        this.toolbar.on('fullscreen:pointerclick', function () { return _this.toggleFullscreen(); });
        this.toolbar.on('minimap:pointerclick', function () { return _this.toggleMinimap(); });
        document.addEventListener('fullscreenchange', function () { return _this.updateToolbarButtons(); });
        this.navigator = new ui.Navigator({
            paperScroller: this.scroller,
            width: 340,
            height: 130,
            padding: 10,
            zoom: false,
            useContentBBox: true,
            paperOptions: {
                async: true,
                autoFreeze: true,
                elementView: NavigatorElementView,
                cellViewNamespace: {},
                viewManagement: {
                    disposeHidden: true,
                },
                // Don't render links in the navigator
                cellVisibility: function (cell) { return !cell.isLink(); },
                background: {
                    color: 'transparent'
                }
            }
        });
        this.navigator.el.addEventListener('transitionend', function () {
            if (_this.transitionCanceled)
                return;
            _this.navigator.updateCurrentView();
        });
        this.navigator.el.addEventListener('transitioncancel', function () {
            _this.transitionCanceled = true;
        });
        this.element.prepend(this.navigator.el);
        this.navigator.render();
        this.toolbar.getWidgetByName('minimap').el.classList.add('active');
    };
    NavigatorService.prototype.isMinimapVisible = function () {
        var _a;
        return !((_a = this.navigator) === null || _a === void 0 ? void 0 : _a.el.classList.contains('hidden'));
    };
    NavigatorService.prototype.fitToScreen = function () {
        this.scroller.zoomToFit({ useModelGeometry: true, padding: 20 });
    };
    NavigatorService.prototype.toggleFullscreen = function () {
        var fullScreenEl = this.toolbar.getWidgetByName('fullscreen').el;
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
            fullScreenEl.classList.add('active');
        }
        else if (document.exitFullscreen) {
            document.exitFullscreen();
            fullScreenEl.classList.remove('active');
        }
    };
    NavigatorService.prototype.showMinimap = function () {
        this.navigator.el.classList.remove('hidden');
        this.transitionCanceled = false;
    };
    NavigatorService.prototype.hideMiniMap = function () {
        this.navigator.el.classList.add('hidden');
    };
    NavigatorService.prototype.toggleMinimap = function () {
        var minimapEl = this.toolbar.getWidgetByName('minimap').el;
        if (this.isMinimapVisible()) {
            this.hideMiniMap();
            minimapEl.classList.remove('active');
        }
        else {
            this.showMinimap();
            minimapEl.classList.add('active');
        }
        this.updateToolbarButtons();
    };
    NavigatorService.prototype.updateToolbarButtons = function () {
        // Minimap
        var minimapButton = this.toolbar.getWidgetByName('minimap');
        if (this.isMinimapVisible()) {
            minimapButton.setTooltip('Hide minimap');
        }
        else {
            minimapButton.setTooltip('Show minimap');
        }
        // Full screen
        var fullscreenButton = this.toolbar.getWidgetByName('fullscreen');
        if (document.fullscreenElement) {
            fullscreenButton.setIcon("".concat(baseUrl, "/exit-fullscreen.svg"));
            fullscreenButton.setTooltip('Exit full screen');
        }
        else {
            fullscreenButton.setIcon("".concat(baseUrl, "/request-fullscreen.svg"));
            fullscreenButton.setTooltip('Toggle full screen');
        }
    };
    return NavigatorService;
}());
export { NavigatorService };
var templateObject_1;
